/**
 * The name that identifies this app outside of this repository
 */
export const AppName = 'storefront-web-vnext';

/**
 * Release number for the running version of the app
 */
export const AppRelease = __APP_RELEASE__;

/**
 * Environment the app runs in (eg. production, preview, local)
 */
export const AppEnvironment = __APP_ENV__ as 'local' | 'preview' | 'production';

/**
 * Configured log level for the app (error, warn, info, debug)
 */
export const LogLevel = __LOG_LEVEL__;

/**
 * String to use for sending the the `Service-Name` header.
 * See: https://www.notion.so/holdbar/Standardizing-identification-for-Internal-Service-Calls-1e6657695d5980fcad78e44ad5d1b117
 */
export const AppServiceNameHeader = `storefront/web-vnext:${AppRelease}`;

// Discovers every Crowdin-delivered locale file at build time. Non-eager: we
// only need the filenames — `import.meta.glob` returns those synchronously as
// object keys without importing the JSON — so this stays safe to use from
// client-bundled code (this file is imported by the client) without pulling
// any translation content into the bundle.
const localeFiles = import.meta.glob('./domain/i18n/strings/*.json');
const DiscoveredLocales = Object.keys(localeFiles)
  .map((path) => path.match(/\/([a-z]{2}-[A-Z]{2})\.json$/)?.[1])
  .filter((locale): locale is string => locale !== undefined)
  .sort();

// Every locale the storefront UI has a translation file for — i.e. every
// locale that's safe to render (URL routing, `messages.server.ts`). There is
// no "held back" state and no maintained list: a locale goes live the moment
// Crowdin delivers its file under `app/domain/i18n/strings/`. Visiting
// `/fr-FR/...` directly renders fine as soon as `fr-FR.json` exists here.
//
// This is deliberately NOT the same question as "which languages does this
// company's LanguageSwitcher offer" — a company may configure a language in
// backoffice that has no storefront translation at all (or the reverse: not
// every translated locale is one every company has opted into). That's a
// per-company exposure question, answered once, at the seam where a
// company's configured languages meet this list — see `mapStorefront` in
// `~/domain/core/index.server.ts`, which intersects `companyProfile.languages`
// with `SupportedLocales` via `normaliseLocale`. Nothing else in the
// storefront needs its own "is this locale launched" concept.
export const SupportedLocales = DiscoveredLocales;

// Locales usable for spoken-language event tagging (e.g. "this tour is
// conducted in Catalan") — a separate business question from "is the
// storefront UI translated into this locale". Today this happens to equal
// `SupportedLocales` exactly, because we only ever discover locale files that
// are simultaneously translated and available; kept as its own named export
// (rather than an alias) because the two concepts could diverge again (e.g.
// a locale used for spoken-language tagging before its UI translation
// lands).
export const SupportedSpokenLocales = DiscoveredLocales;

/**
 * The default locale used when nothing else can be found from a company profile
 */
export const SystemDefaultLocale = 'en-GB';

/**
 * The default country to use when a company profile does not have one set.
 */
export const SystemDefaultCountry = 'dk';

export const SystemRootDomain = __ROOT_DOMAIN__;
